/*  
This component downloads the displayed table in pdf format. This component is called from PltVersionTable.js, CsVersionTable.js file and 
rendered as part of platform dashboard page, customer solution page.

This downloaded pdf file has a title, last updated date and time of the contents of the table and finally contents of the table. 
Updated to include user count when provided.
*/

import React from 'react';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import './VersionDownload.css';
import { FaDownload } from "react-icons/fa6";

const VersionDownload = ({ versionData, versionTime, versionTableTitle, columns, columnKeys, filenamePrefix, userCount, activeUserCount }) => {
  const generatePDF = () => {
    const doc = new jsPDF('landscape');
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    
    const marginSize = 20; // You can adjust this value as needed
    const contentWidth = pageWidth - (2 * marginSize);

    const drawCacheTime = () => {
      doc.setFontSize(10);
      const timeText = `Date and Time: ${versionTime || 'N/A'}`;
      doc.text(timeText, pageWidth - (marginSize + 60), 10);
    };

    const drawUserCount = () => {
      if (userCount !== undefined && userCount !== null) {
        doc.setFontSize(10);
        let userCountText = `Total Users: ${userCount}`;
        
        // Add active user count if provided
        if (activeUserCount !== undefined && activeUserCount !== null) {
          userCountText += ` | Active Users: ${activeUserCount}`;
        }
        
        doc.text(userCountText, pageWidth - (marginSize + 40), 15);
        return true; // Return true to indicate user count was drawn
      }
      return false; // Return false to indicate no user count was drawn
    };

    const drawHeader = () => {
      doc.setFontSize(16);
      doc.setFont(undefined, 'bold');
      doc.text(`${versionTableTitle} Table`, pageWidth / 2, 20, { align: "center" });
      
      doc.setFontSize(12);
      doc.setFont(undefined, 'normal');
      
      // Return different Y positions based on whether user count is shown
      const hasUserCount = drawUserCount();
      return hasUserCount ? 50 : 45;
    };

    if (!versionData || versionData.length === 0) {
      drawCacheTime();
      drawUserCount();
      doc.text("No data available", marginSize, 50);
      doc.save(`${filenamePrefix}_details.pdf`);
      return;
    }

    const data = versionData.map(item => 
      columnKeys.map(key => item[key] !== undefined ? item[key] : (key === "stg" ? "N/A" : "-"))
    );

    drawCacheTime();
    const startY = drawHeader();

    const totalColumns = columns.length;
    const columnWidth = contentWidth / totalColumns;

    doc.autoTable({
      head: [columns],
      body: data,
      startY: startY,
      styles: {
        fontSize: 10,
        cellPadding: 4,
        lineWidth: 0.1,
        lineColor: [80, 80, 80],
        halign: 'left'
      },
      headStyles: {
        fillColor: [20, 66, 66],
        textColor: [255, 255, 255],
        fontStyle: 'bold',
        halign: 'center'
      },
      columnStyles: {
        ...Array.from({ length: totalColumns }, (_, i) => ({
          [i]: { cellWidth: columnWidth }
        })).reduce((acc, curr) => ({ ...acc, ...curr }), {})
      },
      margin: { 
        top: 25, 
        left: marginSize, 
        right: marginSize 
      },
      didDrawPage: (data) => {
        const pageNumber = doc.internal.getCurrentPageInfo().pageNumber;
        const totalPages = doc.internal.getNumberOfPages();
        doc.setFontSize(8);
        doc.text(
          `Page ${pageNumber} of ${totalPages}`, 
          marginSize, 
          pageHeight - 10
        );
        drawCacheTime();
        
        // Only draw user count on first page or if it's a continuation
        if (pageNumber === 1 || (userCount !== undefined && userCount !== null)) {
          drawUserCount();
        }
      }
    });

    const timestamp = new Date().toISOString().split('T')[0];
    const fileName = `${filenamePrefix}_${timestamp}.pdf`;

    doc.save(fileName);
  };

  return (
    <button 
      onClick={generatePDF}
      className="download-version-button"
      title="Download PDF"
    >
      <FaDownload />
    </button>
  );
};

export default VersionDownload;